import { useDispatch, useSelector } from 'react-redux';
import {
    closePatientForm,
    ctxBlockExitForm,
    ctxForceSelectLastForm,
    ctxFormMultiContainerOpen,
    ctxFullFormOpen,
    ctxFullFormsQueue,
    ctxSplitScreen,
    setBlockExitForm,
    setDischargeTemplate,
    setEMedLoad,
    setFormMultiContainerOpen,
    setFullFormOpen,
    setLoadId,
} from '../../../../../store/reducers/patientContext';
import _ from 'lodash';
import { contextFormsList } from './formsContextList';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { hasOneOfPermission, sendUserAudit, user } from '../../../../../store/reducers/user';
import { setSnackbar } from '../../../../../store/reducers/globals';
import FormErrorPage from '../../../../error/customErrorPages/formErrorPage';
import ErrorBoundary from '../../../../../controls/errorBoundary';
import FullFormContextPanel from './fullFormContextPanel';
import { Box, Divider } from '@mui/material';
import clsx from 'clsx';
import { getTabHeight } from '../../../../../util/patientContextUtil';
import MessageBox from '../../../../../util/messageBox';
import useOpenState from '../../../../../hooks/useOpenState';
import makeStyles from '@mui/styles/makeStyles';
import { agylePref } from '../../../../../store/reducers/preferences';
import moment from 'moment/moment';
import Formatter from '../../../../../util/formatter';
import MultiEntriesFormContainer from '../multiEntryComponent/multiEntriesFormContainer';
import { generatePdf } from '../../../../../util/dorGenerator';
import { additionalData } from '../../../../../store/reducers/additionalData';
import { clearWorkflow, nextWorkflowStep, workflow } from '../../../../../store/reducers/workflow';
import { setObsFromForms } from '../../../../../store/reducers/formsAdditionalHelper';
import { AUDIT_TYPES } from '../../../../../util/auditUtil';

const useStyles = makeStyles((theme) => ({
    root: {
        backgroundColor: theme.palette.inPatient.formBackground,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'column',
        gap: 10,
        padding: 16,
        borderRadius: 6,
    },
}));

const FullFormContainer = (props) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const {
        idElement,
        handleSaveData,
        handleDischargePatient,
        handleSaveAdditionalData,
        modifyPatient,
        openFormId,
        index,
        modifyLastObs,
        screen,
        ...otherProps
    } = props;

    // Selectors
    const isFullFormOpen = useSelector(ctxFullFormOpen);
    const agylePreference = useSelector(agylePref);
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);
    const patientAddData = useSelector(additionalData);
    const isBlockExitForm = useSelector(ctxBlockExitForm);
    const forceSelectLastForm = useSelector(ctxForceSelectLastForm);
    const formQueue = useSelector(ctxFullFormsQueue);
    const workflowData = useSelector(workflow);
    const isMultiContainerOpen = useSelector(ctxFormMultiContainerOpen);
    const splitScreen = useSelector(ctxSplitScreen);

    // Form list
    const fullForms = _.filter(contextFormsList, (f) => !f.isMiniForm);
    const [selectedForm, setSelectedForm] = useState(null);
    const [selectedHistoryForm, setSelectedHistoryForm] = useState(null);
    const isFormModify = useRef(false);

    // Button
    const [buttonDisabled, setButtonDisabled] = useState(props.disableSave);
    const [turnOffSubmit, setTurnOffSubmit] = useState(false);
    const [customButtons, setCustomButtons] = useState([]);
    const [submitFunction, setSubmitFunction] = useState(null);
    const [submitButtonText, setSubmitButtonText] = useState('Submit');

    const [isWarningOpen, openWarning, closeWarning] = useOpenState(false, true);

    // Scroll check
    const containerRef = useRef(null);
    const [hasScrollbar, setHasScrollbar] = useState(false);
    const showOnBottom = agylePreference?.formActionBarMode === 'withBottom';

    // Container width
    const containerWidthRef = useRef(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const updateSize = () => {
            if (containerWidthRef.current) {
                setWidth(containerWidthRef.current?.getBoundingClientRect()?.width);
            }
        };

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, [containerWidthRef.current, splitScreen?.enabled]);

    useEffect(() => {
        if (!_.isEmpty(openFormId)) {
            let history = false;
            let formId = openFormId;
            if ((splitScreen.enabled && splitScreenQueueExists(formId)) || !splitScreen?.enabled) {
                if (_.startsWith(formId, '$multi_')) {
                    history = true;
                    formId = _.replace(formId, '$multi_', '');
                    if (formId.includes('_$id_')) {
                        const split = formId.split('_$id_');
                        formId = split[0];
                        dispatch(setLoadId(split[1]));
                    }
                }

                const findForm = _.find(fullForms, { id: formId });
                if (findForm && findForm?.enabled) {
                    let checkTags = true;
                    if (!_.isEmpty(findForm?.options?.disableForPatientTags) && !_.isEmpty(props.patient?.patient?.tags)) {
                        checkTags = !_.some(props.patient?.patient?.tags, (tag) =>
                            _.includes(findForm?.options?.disableForPatientTags, tag),
                        );
                    }

                    if (!checkTags) {
                        dispatch(
                            setSnackbar({
                                type: 'warning',
                                message: t('Patient is not allowed to access this form.'),
                            }),
                        );
                        dispatch(closePatientForm(formId));
                        return;
                    }

                    if (
                        _.isEmpty(findForm.requiredPermission) ||
                        (!_.isEmpty(findForm.requiredPermission) && dispatch(hasOneOfPermission(findForm.requiredPermission)))
                    ) {
                        setSelectedForm(findForm);
                        if (history) {
                            dispatch(setFormMultiContainerOpen(true));
                        } else {
                            dispatch(setFullFormOpen(true));
                        }
                    } else {
                        dispatch(
                            setSnackbar({
                                type: 'warning',
                                message: t('User does not have permission to open this form.'),
                            }),
                        );
                    }
                    // dispatch(closePatientForm(formId));
                }
            }
        }
    }, [openFormId, splitScreen?.enabled, screen]);

    useEffect(() => {
        if (forceSelectLastForm) setSelectedHistoryForm(forceSelectLastForm);
    }, [forceSelectLastForm]);

    useEffect(() => {
        if (!isFullFormOpen && !isMultiContainerOpen && selectedForm) {
            setSelectedForm(null);
        }
    }, [isFullFormOpen, selectedForm, openFormId]);

    useEffect(() => {
        dispatch(setBlockExitForm(selectedForm?.options?.blockExit && !isMultiContainerOpen));
    }, [selectedForm, isMultiContainerOpen]);

    useEffect(() => {
        const element = containerRef.current;
        if (!element || agylePreference?.formActionBarMode !== 'withBottom') return;

        const checkScrollbar = () => {
            if (element && isFullFormOpen) {
                setHasScrollbar(element.clientHeight < element.scrollHeight);
            }
        };

        checkScrollbar();

        // Setting up the MutationObserver to monitor changes in the container
        const observer = new MutationObserver((mutationsList, observer) => {
            checkScrollbar();
        });

        observer.observe(element, {
            childList: true,
            subtree: true,
            attributes: true,
            characterData: true,
        });

        window.addEventListener('resize', checkScrollbar);
        return () => {
            observer.disconnect();
            window.removeEventListener('resize', checkScrollbar);
        };
    }, [containerRef, isFullFormOpen, agylePreference?.formActionBarMode]);

    // Blocking closing and refreshing the agyla card
    useEffect(() => {
        const handleBeforeUnload = (event) => {
            if (isBlockExitForm) {
                event.preventDefault();
                event.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        if (!isBlockExitForm) {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        }
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isBlockExitForm]);

    function splitScreenQueueExists(formId) {
        const queue = screen === 1 ? splitScreen.screenOneForms : splitScreen.screenTwoForms;
        return _.includes(queue, formId);
    }

    // TODO check to queue for another form to open
    const handleCloseForm = () => {
        if (isFormModify.current) {
            openWarning();
        } else {
            clearStates();
        }
    };

    const handleForceClose = () => {
        dispatch(clearWorkflow());
        closeWarning();
        clearStates();
    };

    const validateMultipleFormObject = (obj) => {
        if (_.isArray(obj) && !_.isEmpty(obj)) {
            if (obj[0].rePrint) return true;
            return obj[0].hasOwnProperty('value') && obj[0].hasOwnProperty('noteToAddWithRefId') && obj[0].hasOwnProperty('referenceId');
        }
        return false;
    };

    const handleSaveChanges = (values) => {
        if (selectedForm?.options?.isMultipleForm && selectedForm?.options?.dataSaveDestination === 'piw') {
            if (validateMultipleFormObject(values)) {
                const isRePrint = values[0].rePrint;
                let data;
                if (isRePrint) {
                    data = [
                        {
                            name: values[0].name,
                            value: values[0].value,
                            noteToAdd: values[0].noteToAdd,
                            idToTrack: values[0]?.idToTrack || null,
                        },
                    ];
                } else {
                    const oldList = _.get(props.patient, values[0].name);
                    const cloneList = _.cloneDeep(oldList?.list || (!_.isEmpty(oldList) ? [oldList] : []));
                    cloneList.unshift(values[0].value);
                    data = [
                        {
                            name: values[0].name,
                            value: {
                                list: [...cloneList],
                                by: userFullName,
                                login: userObject?.userName,
                                date: moment().toISOString(),
                            },
                            noteToAddWithRefId: [
                                {
                                    noteToAddWithRefId: values[0].noteToAddWithRefId,
                                    referenceId: values[0].referenceId,
                                },
                            ],
                            idToTrack: values[0]?.idToTrack || null,
                        },
                    ];
                }
                if (values.length > 1) {
                    data = data.concat(values.slice(1));
                }

                if (data?.[0]?.name === 'studies.auditC' && modifyPatient) {
                    modifyPatient(data);
                }

                if (data?.[0]?.name === 'studies.prescription') {
                    const piwForPDF = _.set(_.cloneDeep(props.patient), data[0].name, data[0].value);
                    dispatch(
                        sendUserAudit(props.patient, isRePrint ? AUDIT_TYPES.PRESCRIPTION_REPRINT : AUDIT_TYPES.PRESCRIPTION_CREATE, {
                            idToTrack: data[0]?.idToTrack,
                        }),
                    );
                    dispatch(setSnackbar({ type: 'info', message: t('Printing Prescription') }));
                    generatePdf(piwForPDF, patientAddData, 'customPrescriptionPrint', { prescriptionId: data[0].idToTrack })
                        .then((res) => {
                            if (res)
                                dispatch(
                                    setSnackbar({
                                        type: 'success',
                                        message: 'The prescription document was successfully generated.',
                                    }),
                                );
                            else
                                dispatch(
                                    setSnackbar({
                                        type: 'error',
                                        message: 'An error occurred while generating the prescription document.',
                                    }),
                                );
                        })
                        .catch((e) => {
                            console.error(e);
                            dispatch(setSnackbar({ type: 'error', message: e }));
                        });
                }

                handleSaveData(data);
            } else {
                dispatch(setSnackbar({ type: 'error', message: 'The form could not be saved.' }));
            }
        } else {
            if (selectedForm?.options?.dataSaveDestination === 'patientData') {
                handleSaveAdditionalData(values);
                handleForceClose();
            } else {
                const auditCModification = _.find(values, { name: 'studies.auditC' });
                if (auditCModification && modifyPatient) {
                    modifyPatient([auditCModification]);
                }
                handleSaveData(values);
            }
        }
    };

    const onDischargePatient = (values) => {
        handleDischargePatient(values);
        clearStates();
    };

    const handleSubmitFunction = useCallback((func) => {
        setSubmitFunction(() => func);
    }, [setSubmitFunction]);

    const handleModifyForm = () => {
        isFormModify.current = true;
    };

    const handleSaveForm = () => {
        if (submitFunction) {
            const valid = submitFunction();
            if (_.isUndefined(valid) || valid) {
                if (Object.prototype.toString.call(valid) === '[object Promise]') {
                    valid
                        .then((res) => {
                            if (res) {
                                isFormModify.current = false;
                                handleCloseForm();
                            }
                        })
                        .catch((e) => {
                            console.error(e);
                            // dispatch(setSnackbar({ type: 'error', message: e }));
                        });
                } else {
                    isFormModify.current = false;
                    handleCloseForm();
                }
            }
        } else {
            console.error('Submit form function not found');
            dispatch(setSnackbar({ type: 'error', message: 'Unable to save form' }));
        }
    };

    const clearStates = () => {
        dispatch(closePatientForm(openFormId));
        isFormModify.current = false;
        dispatch(setEMedLoad(false));
        dispatch(setBlockExitForm(false));
        dispatch(setLoadId(null));
        dispatch(setObsFromForms(false));
        dispatch(setDischargeTemplate(null));
        setSelectedForm(null);
        setSelectedHistoryForm(null);
        setCustomButtons([]);
        setSubmitFunction(null);
        setSubmitButtonText('Submit');
        setTurnOffSubmit(false);
        dispatch(setFormMultiContainerOpen(false));
        if (workflowData?.type === openFormId) {
            dispatch(nextWorkflowStep());
        }
    };

    const getFormHeight = () => {
        const contextHeight = getTabHeight(idElement);
        const header = document.getElementById('full-form-header')?.offsetHeight || 60;
        return window.innerHeight - (header + contextHeight?.height + 25);
    };

    const handleDisableSubmit = (val) => {
        if (!props.disableSave) {
            setButtonDisabled(val);
        }
    };

    const handleCustomButtons = (buttons) => {
        setCustomButtons(buttons || []);
    };

    const handleSaveFromInside = (values) => {
        handleSaveChanges(values);
        handleForceClose();
    };

    const handleCloseMultiList = (closeForm = true) => {
        dispatch(setFormMultiContainerOpen(false));
        if (closeForm) dispatch(closePatientForm(openFormId));
    };

    const getDisplay = () => {
        if (splitScreen?.enabled) {
            let queue = screen === 1 ? splitScreen.screenOneForms : splitScreen.screenTwoForms;
            queue = _.intersection(formQueue, queue);
            const length = queue.length - 1;
            const index = _.findIndex(queue, (f) => {
                let splitFormId = f;
                if (_.startsWith(splitFormId, '$multi_')) {
                    splitFormId = _.replace(splitFormId, '$multi_', '');
                }

                if (splitFormId.includes('_$id_')) {
                    const splitFormIdArray = splitFormId.split('_$id_');
                    splitFormId = splitFormIdArray[0];
                }
                return splitFormId === selectedForm.id;
            });
            return index === length ? 'auto' : 'hidden';
        } else {
            const length = formQueue.length - 1;
            return index === length ? 'auto' : 'hidden';
        }
    };

    if ((!isFullFormOpen && !isMultiContainerOpen) || !selectedForm) return null;
    // TODO @Mateusz disable submit if not modify
    return (
        <div style={{ contentVisibility: getDisplay(), position: 'sticky' }} ref={containerWidthRef}>
            <FullFormContextPanel
                form={selectedForm}
                onClose={handleCloseForm}
                onSave={handleSaveForm}
                disableSave={buttonDisabled}
                buttons={customButtons}
                patientType={props.patientType}
                patientAgeType={props.patientAgeType}
                customSubmitButtonText={submitButtonText}
                hideSubmit={turnOffSubmit}
            />

            <Divider sx={{ borderWidth: 1 }} />

            <div
                style={{
                    padding: '10px 88px',
                    height: getFormHeight(),
                }}
                className={clsx('tabs-no-scroll', 'scroll-normal-white')}
                ref={containerRef}
            >
                <ErrorBoundary errorPage={<FormErrorPage />}>
                    {isFullFormOpen && (
                        <Box
                            className={selectedForm?.options?.rootStyle ? undefined : classes.root}
                            sx={selectedForm?.options?.rootStyle ? (theme) => selectedForm.options.rootStyle(theme) : undefined}
                        >
                            {React.cloneElement(selectedForm?.context, {
                                open: isFullFormOpen,
                                onChange: handleSaveChanges,
                                onDischargePatient: onDischargePatient,
                                onModify: handleModifyForm,
                                onDisableSubmit: handleDisableSubmit,
                                onCustomButtons: handleCustomButtons,
                                onSubmitFunction: handleSubmitFunction,
                                onHideSubmit: setTurnOffSubmit,
                                selectedHistoryForm: selectedHistoryForm,
                                onCustomSubmitText: setSubmitButtonText,
                                modifyLastObs: modifyLastObs,
                                modifyPatient: modifyPatient,
                                _onForceClose: handleForceClose,
                                _customOnSave: handleSaveFromInside,
                                _formOptions: selectedForm.options,
                                _isFullFormMode: true,
                                _containerWidth: width,
                                _containerScrollRef: containerRef,
                                ...otherProps,
                            })}
                        </Box>
                    )}

                    {showOnBottom && hasScrollbar && (
                        <FullFormContextPanel
                            form={selectedForm}
                            onClose={handleCloseForm}
                            onSave={handleSaveForm}
                            disableSave={buttonDisabled}
                            buttons={customButtons}
                            patientType={props.patientType}
                            patientAgeType={props.patientAgeType}
                            customSubmitButtonText={submitButtonText}
                            hideSubmit={turnOffSubmit}
                            disableTitle
                        />
                    )}

                    {isMultiContainerOpen && !!selectedForm && (
                        <MultiEntriesFormContainer
                            open={isMultiContainerOpen}
                            onClose={handleCloseMultiList}
                            form={selectedForm}
                            patient={props.patient}
                            patientType={props.patientType}
                            patientAgeType={props.patientAgeType}
                            disableSave={props.disableSave}
                            onLoadHistory={setSelectedHistoryForm}
                            _onForceClose={handleForceClose}
                            _customOnSave={handleSaveFromInside}
                        />
                    )}
                </ErrorBoundary>
            </div>

            <MessageBox
                open={isWarningOpen}
                onClose={closeWarning}
                title={t('Warning')}
                // haveAccept={{yes: t('Close and lose data'), no: t('Stay on the form')}}
                haveAccept={{ yes: t('Close form'), no: t('Remain on form') }}
                onAccept={handleForceClose}
                message={t(
                    'You currently have a form open. If you close this form, any unsaved data will be lost. Are you sure you want to continue?',
                )}
                warning
            />
        </div>
    );
};

export default FullFormContainer;
